import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Logo, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const field = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-base text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

const TOOL_OPTS = [
  { value: "very_difficult", label: "Very difficult" },
  { value: "somewhat_difficult", label: "Somewhat difficult" },
  { value: "neutral", label: "Neutral" },
  { value: "helpful", label: "Helpful" },
  { value: "very_helpful", label: "Very helpful" },
];
const EXPECT_OPTS = [
  { value: "fell_short", label: "Fell short" },
  { value: "fell_slightly_short", label: "Fell slightly short" },
  { value: "met", label: "Met expectations" },
  { value: "exceeded", label: "Exceeded expectations" },
];

const EMPTY = {
  name: "", bookingConfirmation: "", overallRating: 0,
  bookingSatisfaction: 0, likedMost: "", frustrating: "", commSatisfaction: 0, feltSupported: null,
  toolsInvoices: "", toolsItinerary: "", toolsPayments: "",
  expectationsMet: "", aspectResort: 0, aspectTransportation: 0, aspectActivities: 0, aspectDining: 0, aspectValue: 0, tripIssues: "",
  recommend: 0, bookAgain: null, testimonial: null, improve: "", anythingElse: "",
};

function StarInput({ value, onChange, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid={testid} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} onMouseEnter={() => setHover(n)} onClick={() => onChange(n)} data-testid={`${testid}-${n}`} className="p-0.5">
          <Star className={cn("h-9 w-9 transition-colors", (hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300")} />
        </button>
      ))}
    </div>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {[["yes", "Yes", true], ["no", "No", false]].map(([k, l, v]) => (
        <button type="button" key={k} onClick={() => onChange(v)} data-testid={`${testid}-${k}`}
          className={cn("h-11 flex-1 rounded-xl border text-sm font-semibold transition-colors", value === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-navy-900 hover:bg-slate-50")}>{l}</button>
      ))}
    </div>
  );
}

function Choice({ options, value, onChange, testid }) {
  return (
    <div className="grid gap-2" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={cn("flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors", value === o.value ? "border-navy-900 bg-navy-50 text-navy-900" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
          <span className={cn("grid h-4 w-4 shrink-0 place-items-center rounded-full border", value === o.value ? "border-navy-900" : "border-slate-300")}>{value === o.value && <span className="h-2 w-2 rounded-full bg-navy-900" />}</span>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Q({ label, hint, children }) {
  return <div className="space-y-2.5"><p className="text-sm font-semibold text-navy-900">{label}{hint && <span className="ml-1 font-normal text-slate-400">{hint}</span>}</p>{children}</div>;
}

const PAGE_TITLES = ["Overall Experience", "Booking Process", "Tools & Usability", "Trip Experience", "Final Feedback"];

export default function SurveyPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`/survey/${token}`).then(({ data }) => {
      setMeta(data);
      setF((p) => ({ ...p, name: data.clientName || "", bookingConfirmation: data.bookingConfirmation || "" }));
      if (data.completed) setDone(true);
    }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const validate = () => {
    if (page === 0) {
      if (!f.name.trim()) return "Please enter your name";
      if (!f.bookingConfirmation.trim()) return "Please enter your booking confirmation number";
      if (!f.overallRating) return "Please give an overall rating";
    }
    if (page === 1) {
      if (!f.bookingSatisfaction) return "Please rate the booking process";
      if (!f.commSatisfaction) return "Please rate communication before and during your trip";
      if (f.feltSupported === null) return "Please tell us if you felt supported";
    }
    if (page === 2) {
      if (!f.toolsInvoices || !f.toolsItinerary || !f.toolsPayments) return "Please rate all three tools";
    }
    if (page === 3) {
      if (!f.expectationsMet) return "Please tell us how the trip met your expectations";
      if (!f.aspectResort || !f.aspectTransportation || !f.aspectActivities || !f.aspectDining || !f.aspectValue) return "Please rate every aspect of your trip";
    }
    if (page === 4) {
      if (!f.recommend) return "Please choose how likely you are to recommend us";
      if (f.bookAgain === null) return "Please tell us if you'd book with us again";
      if (f.testimonial === null) return "Please tell us if we may use your feedback as a testimonial";
    }
    return null;
  };

  const next = async () => {
    const err = validate();
    if (err) { toast.dismiss(); toast.error(err); return; }
    if (page < 4) { setPage((p) => p + 1); window.scrollTo?.(0, 0); return; }
    setBusy(true);
    try {
      await api.post(`/survey/${token}`, f);
      setDone(true);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const shell = (children) => (
    <div className="app-shell scroll-area bg-[#F5F7FA]" data-testid="survey-page">
      <div className="mx-auto flex min-h-full max-w-xl flex-col px-4 py-8 sm:py-12">{children}</div>
    </div>
  );

  if (error) return shell(
    <div className="my-auto flex flex-col items-center text-center" data-testid="survey-error">
      <Logo brand="mytrip" size="h-9" />
      <div className="mt-6 w-full rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
        <h1 className="font-display text-xl font-bold text-navy-900">Survey unavailable</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );
  if (!meta) return shell(<div className="my-auto grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div>);

  if (done) return shell(
    <div className="my-auto rounded-3xl border border-slate-200 bg-white p-8 text-center sm:p-10" data-testid="survey-thank-you">
      <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-7 w-7" /></div>
      <h1 className="font-display text-2xl font-bold text-navy-900">Thank you!</h1>
      <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">Your feedback has been received{meta.tripTitle ? ` for ${meta.tripTitle}` : ""}. We truly appreciate you taking the time — it helps us craft even better journeys.</p>
      <p className="mt-6 text-xs text-slate-400">{meta.agencyName}</p>
    </div>
  );

  const progress = ((page + 1) / 5) * 100;

  return shell(
    <>
      <div className="mb-6 flex flex-col items-center text-center">
        <Logo brand="mytrip" size="h-9" />
        {meta.tripTitle && <p className="mt-3 text-sm font-medium text-slate-500" data-testid="survey-trip-title">Post-trip survey · {meta.tripTitle}</p>}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-400">
            <span data-testid="survey-step-label">Page {page + 1} of 5 · {PAGE_TITLES[page]}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-navy-900 transition-all duration-300" style={{ width: `${progress}%` }} data-testid="survey-progress" />
          </div>
        </div>

        <div className="space-y-6" data-testid={`survey-page-${page}`}>
          {page === 0 && (<>
            <Q label="Your name"><input className={field} value={f.name} onChange={(e) => up("name", e.target.value)} data-testid="survey-name" /></Q>
            <Q label="Booking confirmation number"><input className={field} value={f.bookingConfirmation} onChange={(e) => up("bookingConfirmation", e.target.value)} data-testid="survey-booking" /></Q>
            <Q label="Overall, how would you rate your experience?"><StarInput value={f.overallRating} onChange={(v) => up("overallRating", v)} testid="survey-overall" /></Q>
          </>)}

          {page === 1 && (<>
            <Q label="How satisfied were you with the booking process?"><StarInput value={f.bookingSatisfaction} onChange={(v) => up("bookingSatisfaction", v)} testid="survey-booking-sat" /></Q>
            <Q label="What did you like most about the booking process?" hint="· optional"><textarea rows={3} className={cn(field, "resize-y")} value={f.likedMost} onChange={(e) => up("likedMost", e.target.value)} data-testid="survey-liked-most" /></Q>
            <Q label="Was anything frustrating or confusing during booking?" hint="· optional"><textarea rows={3} className={cn(field, "resize-y")} value={f.frustrating} onChange={(e) => up("frustrating", e.target.value)} data-testid="survey-frustrating" /></Q>
            <Q label="How satisfied were you with communication before and during your trip?"><StarInput value={f.commSatisfaction} onChange={(v) => up("commSatisfaction", v)} testid="survey-comm-sat" /></Q>
            <Q label="Did you feel supported if questions or issues came up?"><YesNo value={f.feltSupported} onChange={(v) => up("feltSupported", v)} testid="survey-felt-supported" /></Q>
          </>)}

          {page === 2 && (<>
            <p className="text-sm text-slate-500">Please rate how helpful and easy to use you found each of our online tools.</p>
            <Q label="Invoices"><Choice options={TOOL_OPTS} value={f.toolsInvoices} onChange={(v) => up("toolsInvoices", v)} testid="survey-tools-invoices" /></Q>
            <Q label="Trip Itinerary"><Choice options={TOOL_OPTS} value={f.toolsItinerary} onChange={(v) => up("toolsItinerary", v)} testid="survey-tools-itinerary" /></Q>
            <Q label="Making Payments"><Choice options={TOOL_OPTS} value={f.toolsPayments} onChange={(v) => up("toolsPayments", v)} testid="survey-tools-payments" /></Q>
          </>)}

          {page === 3 && (<>
            <Q label="How well did the trip meet your expectations?"><Choice options={EXPECT_OPTS} value={f.expectationsMet} onChange={(v) => up("expectationsMet", v)} testid="survey-expectations" /></Q>
            <div className="space-y-4">
              <p className="text-sm font-semibold text-navy-900">Please rate the following aspects of your trip:</p>
              {[["aspectResort", "Resort / Stateroom"], ["aspectTransportation", "Transportation / Ship"], ["aspectActivities", "Activities / Excursions"], ["aspectDining", "Dining / Beverages"], ["aspectValue", "Overall Value"]].map(([k, l]) => (
                <div key={k} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-slate-600">{l}</span>
                  <StarInput value={f[k]} onChange={(v) => up(k, v)} testid={`survey-${k}`} />
                </div>
              ))}
            </div>
            <Q label="Did you experience any issues during your trip?" hint="· optional"><textarea rows={3} className={cn(field, "resize-y")} value={f.tripIssues} onChange={(e) => up("tripIssues", e.target.value)} data-testid="survey-trip-issues" /></Q>
          </>)}

          {page === 4 && (<>
            <Q label="How likely are you to recommend our services to a friend or family?">
              <div className="flex flex-wrap gap-1.5" data-testid="survey-recommend">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button type="button" key={n} onClick={() => up("recommend", n)} data-testid={`survey-recommend-${n}`}
                    className={cn("h-10 w-10 rounded-lg border text-sm font-semibold transition-colors", f.recommend === n ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-navy-900 hover:bg-slate-50")}>{n}</button>
                ))}
              </div>
              <div className="flex justify-between text-[11px] text-slate-400"><span>1 — Not at all likely</span><span>10 — Extremely likely</span></div>
            </Q>
            <Q label="Would you book with us again?"><YesNo value={f.bookAgain} onChange={(v) => up("bookAgain", v)} testid="survey-book-again" /></Q>
            <Q label="May we use your feedback as a testimonial?"><YesNo value={f.testimonial} onChange={(v) => up("testimonial", v)} testid="survey-testimonial" /></Q>
            <Q label="What could we do better next time?" hint="· optional"><textarea rows={3} className={cn(field, "resize-y")} value={f.improve} onChange={(e) => up("improve", e.target.value)} data-testid="survey-improve" /></Q>
            <Q label="Anything else you'd like us to know?" hint="· optional"><textarea rows={3} className={cn(field, "resize-y")} value={f.anythingElse} onChange={(e) => up("anythingElse", e.target.value)} data-testid="survey-anything-else" /></Q>
          </>)}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button type="button" onClick={() => { setPage((p) => Math.max(0, p - 1)); window.scrollTo?.(0, 0); }} disabled={page === 0} className={cn(btn.secondary, page === 0 && "invisible")} data-testid="survey-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
          <button type="button" onClick={next} disabled={busy} className={btn.primary} data-testid="survey-next-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : page === 4 ? <>Submit feedback<CheckCircle2 className="h-4 w-4" /></> : <>Continue<ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </>
  );
}
